import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, Clock, Award, Video, ExternalLink } from 'lucide-react';
import { useModules } from '../contexts/ModuleContext';
import { useAuth } from '../contexts/AuthContext';
import { graphqlService } from '../services/graphqlService';

const TrainingModule = () => {
  const { moduleId } = useParams();
  const { getModule } = useModules();
  const { user } = useAuth();
  const [currentSection, setCurrentSection] = useState(0);
  const [userProgress, setUserProgress] = useState({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  const module = getModule(moduleId);

  useEffect(() => {
    if (!module || !user) return;

    const loadProgress = async () => {
      try {
        const progress = await graphqlService.getProgress(user.email);
        setUserProgress(progress || {});
        if (progress && progress[moduleId]?.completed) {
          setIsCompleted(true);
          // Set current section from progress if available
          if (progress[moduleId]?.currentSection !== undefined) {
            setCurrentSection(progress[moduleId].currentSection);
          }
        }
      } catch (error) {
        console.error('Error loading progress:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProgress();
  }, [moduleId, module, user]);

  if (!module) {
    return (
      <div className="container">
        <div className="card text-center">
          <h2>Module not found</h2>
          <Link to="/" className="btn btn-primary">Return to Dashboard</Link>
        </div>
      </div>
    );
  }

  const handleNext = async () => {
    if (currentSection < module.content.sections.length - 1) {
      const newSection = currentSection + 1;
      setCurrentSection(newSection);
      
      // Update progress in database
      if (user && user.email) {
        try {
          await graphqlService.createOrUpdateProgress({
            userEmail: user.email,
            moduleId: moduleId,
            currentSection: newSection,
            completed: false
          });
        } catch (error) {
          console.error('Error updating progress:', error);
        }
      }
    } else {
      // Mark module as completed
      if (user && user.email) {
        try {
          await graphqlService.createOrUpdateProgress({
            userEmail: user.email,
            moduleId: moduleId,
            completed: true,
            completedAt: new Date().toISOString(),
            currentSection: currentSection
          });
          
          const updatedProgress = {
            ...userProgress,
            [moduleId]: {
              ...userProgress[moduleId],
              completed: true,
              completedAt: new Date().toISOString()
            }
          };
          setUserProgress(updatedProgress);
          setIsCompleted(true);
        } catch (error) {
          console.error('Error marking module as completed:', error);
        }
      }
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handleStartQuiz = () => {
    // Navigate to quiz
    window.location.href = `/quiz/${moduleId}`;
  };

  const currentSectionData = module.content.sections[currentSection];
  const progressPercentage = ((currentSection + 1) / module.content.sections.length) * 100;

  return (
    <div className="container">
      <div className="module-header">
        <Link to="/" className="btn btn-secondary back-to-dashboard">
          <ArrowLeft size={16} style={{ marginRight: '8px' }} />
          Back to Dashboard
        </Link>
        
        <div className="module-info">
          <div className="module-icon-large">
            {React.isValidElement(module.icon) ? module.icon : null}
          </div>
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#0477aa' }}>{module.title}</h1>
            <p className="text-lg">{module.description}</p>
          </div>
        </div>

        <div className="module-meta">
          <div className="meta-item">
            <Clock size={16} />
            <span>{module.duration} minutes</span>
          </div>
          <div className="meta-item">
            <Award size={16} />
            <span>{module.difficulty}</span>
          </div>
        </div>
      </div>

      <div className="progress-section">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
        <p className="text-sm">
          Section {currentSection + 1} of {module.content.sections.length}
        </p>
      </div>

      {isCompleted ? (
        <div className="card completion-card">
          <div className="completion-icon">
            <CheckCircle size={64} color="#059669" />
          </div>
          <h2 className="text-2xl font-bold mb-4">Module Completed!</h2>
          <p className="text-lg mb-6">
            Congratulations! You have successfully completed the {module.title} module.
          </p>
          <div className="completion-actions">
            <button onClick={handleStartQuiz} className="btn btn-primary">
              Take Quiz
            </button>
            <Link to="/" className="btn btn-secondary">
              Return to Dashboard
            </Link>
          </div>
        </div>
      ) : (
        <div className="card content-card">
          <div className="section-header">
            <h2 className="text-2xl font-bold mb-4">{currentSectionData.title}</h2>
          </div>
          
          <div className="section-content">
            {currentSectionData.video && (
              <div className="video-container mb-6">
                {(() => {
                  const videoSrc = currentSectionData.video.src || '';
                  const isSharePoint = videoSrc.includes('sharepoint.com') || videoSrc.includes('sharepoint');
                  
                  if (isSharePoint) {
                    // SharePoint videos cannot be embedded in iframes due to security policies
                    // Show a link to open the video instead
                    return (
                      <div className="sharepoint-video-fallback">
                        <div className="video-fallback-icon">
                          <Video size={64} color="#6b7280" />
                        </div>
                        <div className="video-fallback-content">
                          <h3 className="text-lg font-semibold mb-2">SharePoint Video</h3>
                          <p className="text-gray-600 mb-4">
                            This video is hosted on SharePoint and cannot be embedded directly due to security policies. 
                            Please click the link below to view it in a new window.
                          </p>
                          <a
                            href={videoSrc}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-primary"
                          >
                            <ExternalLink size={16} style={{ marginRight: '8px' }} />
                            Open Video in SharePoint
                          </a>
                        </div>
                        {currentSectionData.video.caption && (
                          <p className="video-caption text-sm mt-4">
                            {currentSectionData.video.caption}
                          </p>
                        )}
                      </div>
                    );
                  }
                  
                  // For non-SharePoint videos, use iframe as before
                  return (
                    <>
                      <div className="video-wrapper">
                        <iframe
                          src={videoSrc}
                          title={currentSectionData.video.title || 'Training Video'}
                          className="training-video"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          onError={(e) => {
                            console.error('Video iframe error:', e);
                          }}
                        ></iframe>
                      </div>
                      {currentSectionData.video.caption && (
                        <p className="video-caption text-sm mt-2">
                          {currentSectionData.video.caption}
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            <div className="content-text">
              {currentSectionData.content.split('\n').map((line, index) => (
                <p key={index} className="mb-4">
                  {line}
                </p>
              ))}
            </div>
          </div>

          <div className="section-navigation">
            <button 
              onClick={handlePrevious}
              disabled={currentSection === 0}
              className="btn btn-secondary"
            >
              <ArrowLeft size={16} style={{ marginRight: '8px' }} />
              Previous
            </button>
            
            <button 
              onClick={handleNext}
              className="btn btn-primary"
            >
              {currentSection === module.content.sections.length - 1 ? 'Complete Module' : 'Next'}
              <ArrowRight size={16} style={{ marginLeft: '8px' }} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingModule;
